import { createAsyncThunk } from '@reduxjs/toolkit';

// Services
import { PermissionSettingsService } from '../../services/permission-settings.service';

// Slice
import { PermissionSettingsSliceActions } from './permission-settings.slice';

// Models
import { ICompanyPermissionSettings, IWorkerPermissionRequest } from '../../interfaces/permission-settings.interfaces';
import { openNotification } from '../app-notifications/app-notifications.thunks';

export enum EPermissionSettingsThunks {
  GetCompanyPermissionSettings = 'PERMISSION_SETTINGS/getCompanyPermissionSettings',
  UpdateCompanyPermissionSettings = 'PERMISSION_SETTINGS/updateCompanyPermissionSettings',
  GetWorkersPermissionSettings = 'PERMISSION_SETTINGS/getWorkersPermissionSettings',
  ClearPermissionSettings = 'PERMISSION_SETTINGS/clearPermissionSettings',
}

export const getCompanyPermissionSettings = createAsyncThunk(
  EPermissionSettingsThunks.GetCompanyPermissionSettings,
  async (_, { dispatch }) => {
    dispatch(PermissionSettingsSliceActions.getCompanyPermissionSettingsRequest());
    const { company_permission_settings, error } = await PermissionSettingsService.fetchPermissionSettings();
    if (!company_permission_settings) {
      dispatch(PermissionSettingsSliceActions.getCompanyPermissionSettingsRequestFailure(error ?? 'Error'));
      return null;
    }
    dispatch(PermissionSettingsSliceActions.getCompanyPermissionSettingsRequestSuccess(company_permission_settings));
    return company_permission_settings;
  }
);

export const updateCompanyPermissionSettings = createAsyncThunk(
  EPermissionSettingsThunks.UpdateCompanyPermissionSettings,
  async (settings: ICompanyPermissionSettings, { dispatch }) => {
    dispatch(PermissionSettingsSliceActions.getCompanyPermissionSettingsRequest());
    const {
      company_permission_settings,
      error
    } = await PermissionSettingsService.updateCompanyPermissionSettings(settings);
    if (!company_permission_settings) {
      dispatch(openNotification({ type: 'error', description: error ?? 'Error' }));
      return dispatch(PermissionSettingsSliceActions.getCompanyPermissionSettingsRequestFailure(error ?? 'Error'));
    }
    return dispatch(PermissionSettingsSliceActions.getCompanyPermissionSettingsRequestSuccess(company_permission_settings));
  }
);

export const getWorkersPermissionSettings = createAsyncThunk(
  EPermissionSettingsThunks.GetWorkersPermissionSettings,
  async ({ isLoadMore = false, offset, limit }: { isLoadMore?: boolean, offset?: number, limit?: number }, { dispatch }) => {
    dispatch(PermissionSettingsSliceActions.getCompanyPermissionWorkersRequest());
    const {
      company_permission_setting_workers, total, total_all,
      error
    } = await PermissionSettingsService.fetchWorkersPermissionSettings(offset, limit);
    if (!company_permission_setting_workers) {
      dispatch(PermissionSettingsSliceActions.getCompanyPermissionWorkersRequestFailure(error ?? 'Error'));
      return null;
    }
    dispatch(PermissionSettingsSliceActions.getCompanyPermissionWorkersRequestSuccess({
      list: company_permission_setting_workers,
      isLoadMore,
      total: total ?? 0,
      total_all: total_all ?? 0
    }));
    return company_permission_setting_workers;
  }
);

export const updateWorkersPermissionSettings = createAsyncThunk(
  EPermissionSettingsThunks.UpdateCompanyPermissionSettings,
  async (data: IWorkerPermissionRequest, { dispatch }) => {
    dispatch(PermissionSettingsSliceActions.getCompanyPermissionWorkersRequest());
    const {
      company_permission_setting_workers,
      error
    } = await PermissionSettingsService.updateWorkersPermissionSettings(data);
    if (!company_permission_setting_workers) {
      return dispatch(PermissionSettingsSliceActions.getCompanyPermissionWorkersRequestFailure(error ?? 'Error'));
    }
    return dispatch(PermissionSettingsSliceActions.updateWorkersPermissionSettings(company_permission_setting_workers));
  }
);

export const clearPermissionSettings = createAsyncThunk(
  EPermissionSettingsThunks.ClearPermissionSettings,
  async (_, { dispatch }) => {
    return dispatch(PermissionSettingsSliceActions.clearPermissionSettings());
  }
);
